import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { chevronLeft16, chevronRight16 } from "@esri/calcite-ui-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FreeMode } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { getLayerTitle } from "../../esri/custom-popup-content";
import { isWidgetDisplayed } from "../../esri/widgets/expandUtils";
import { showpublicFieldName, view } from "../../utils/API";
import { getOpsColor, isTouchScreen, ROLE_EDITOR } from "../../utils/helper";
import LayerIcon from "../Icons/LayerIcon";
import { Tooltip } from "../UI";
import { ManualWidgetWrapper, MapFilterButton } from "./ManualWidget-styled";
import MapFilter from "./MapFilter/MapFilter";

const ManualWidget = ({ config, t }) => {
  const filters = useSelector((state) => state.filters);
  const printWidget = useSelector((state) => state.printWidget);
  const activeModule = useSelector((state) => state.activeModule);
  const layersLoading = useSelector((state) => state.layersLoading);
  const mobileReducer = useSelector((state) => state.mobileReducer);
  const [showNext, setShowNext] = useState(false);
  const [showPrev, setShowPrev] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [moduleOptionalLayers, setModuleOptionalLayers] = useState([]);

  let [counter, setCounter] = useState(0);

  const [size, setSize] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const viewDiv = document.getElementById("viewDiv");
    if (!viewDiv) return;
    const observer = new ResizeObserver(() => {
      setSize({
        width: viewDiv.offsetWidth,
        height: viewDiv.offsetHeight,
      });
    });
    observer.observe(viewDiv);
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    addLayers(view?.map?.layers);

    view?.map?.layers?.on("change", (event) => {
      addLayers(event.added);
    });
  }, []);

  const addLayers = (layers) => {
    layers?.forEach((layer) => {
      layer.load().then((layer) => {
        setCounter(counter++);
        reactiveUtils.watch(
          () => layer.visible,
          () => setCounter(counter++)
        );
      });
    });
  };

  const swiper = useRef();
  const ref = useRef();

  const onNext = () => {
    swiper.current?.slideNext();
    if (!showPrev) {
      setShowPrev(true);
    }
  };

  const onPrev = () => {
    swiper.current?.slidePrev();
    if (!showNext) {
      setShowNext(true);
    }
  };

  const handleLayerSelection = (layer) => {
    const visibility = !layer.visible;

    layer.visible = visibility;
    layer.labelsVisible = showLabels;

    view.map.layers.forEach((l) => {
      if (
        l.isLabel &&
        l.layerConfigAlias === layer?.layerConfig.alias &&
        layer?.layerConfig?.id === l.layerConfigId
      ) {
        l.visible = visibility;
      }
    });

    layer.load().then(() => {
      setCounter(counter++);
    });
  };

  useEffect(() => {
    if (!view || layersLoading) return;

    const optionalLayers = config.modules[activeModule]?.optionalLayers || [];
    const res = [];
    view.map.layers.forEach((layer) => {
      if (
        optionalLayers.includes(layer.layerConfig?.alias) &&
        !res.some((l) => l.layerConfig?.id === layer.layerConfig?.id)
      ) {
        res.push(layer);
      }
    });

    setModuleOptionalLayers(res);
  }, [activeModule, config, view, layersLoading]);

  const labelLayers = useMemo(() => {
    return view.map.layers.filter((layer) => {
      return layer.layerConfig && layer.layerConfig.labelField && layer.visible;
    });
  }, [activeModule, config, layersLoading, view, counter]);

  const getVisibleFilters = () => {
    let fields = {};
    const mapKeysArray = config.filterFields.filter(
      (item) => item.shownOnMap && item.name == showpublicFieldName
    );

    view.map.layers.forEach((layer) => {
      layer.fields &&
        layer.visible &&
        filters[activeModule] &&
        Object.keys(filters[activeModule]).forEach((filterField) => {
          layer.fields &&
            layer.fields
              .filter((field) => field.name === filterField)
              .forEach((field) => {
                const mapField =
                  mapKeysArray.find((item) => item.name === field.name) ||
                  layer.layerConfig?.filterFields?.find(
                    (item) =>
                      item.name === field.name &&
                      item.shownOnMap &&
                      item.name == showpublicFieldName
                  );
                if (mapField) {
                  fields[field.name] = {
                    ...mapField,
                    field: field,
                    layer,
                  };
                }
              });
        });
    });

    if (!window.isSmall && Object.keys(fields).length > 0) {
      fields.all = {
        type: "all",
      };
    }

    return fields;
  };

  const visibleFields = getVisibleFilters();

  const layersWithSymbology = useMemo(() => {
    const multipleSymbologyLayer = [];
    const addedLayersId = [];

    view.map.layers.forEach((layer) => {
      if (
        Array.isArray(layer.layerConfig?.symbology) &&
        !multipleSymbologyLayer.some(
          (l) => l.layerConfig?.id === layer.layerConfig?.id
        )
      ) {
        multipleSymbologyLayer.push(layer);
        addedLayersId.push(layer?.layerConfig.id);
      }
    });

    return multipleSymbologyLayer;
  }, [activeModule, config, layersLoading, view, counter]);

  const isReady = useMemo(() => {
    return (
      moduleOptionalLayers.length > 0 ||
      Object.keys(visibleFields).length > 0 ||
      (labelLayers.length > 0 && !mobileReducer.isMobileApp) ||
      layersWithSymbology.length > 0
    );
  }, [
    moduleOptionalLayers,
    visibleFields,
    labelLayers,
    layersWithSymbology,
    mobileReducer.isMobileApp,
  ]);

  useEffect(() => {
    if (!swiper.current) return;
    if (
      swiper.current.virtualSize > ref.current?.offsetWidth &&
      !(isTouchScreen() && window.isSmall)
    ) {
      setShowNext(!swiper.current.isEnd);
      setShowPrev(!swiper.current.isBeginning);
    } else {
      setShowNext(false);
      setShowPrev(false);
    }
  }, [isReady, size, visibleFields]);

  const isTouch = useMemo(() => isTouchScreen(), []);
  const hasFilters = useMemo(
    () => isWidgetDisplayed(config, "Filter"),
    [config]
  );
  const opsColor = useMemo(
    () => getOpsColor(config, activeModule),
    [config, activeModule]
  );

  return (
    <ManualWidgetWrapper
      printOpen={printWidget.open && !printWidget.isLoading}
      ref={ref}
      isEditor={config.role === ROLE_EDITOR}
      isMobile={isTouchScreen()}
    >
      {isReady && (
        <>
          {showPrev && (
            <MapFilterButton
              onClick={onPrev}
              opsColor={opsColor}
              style={{
                marginBottom: 4,
                padding: 4,
                position: "absolute",
                left: "-22px",
                zIndex: 2,
                pointerEvents: "auto",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d={chevronLeft16} />
              </svg>
            </MapFilterButton>
          )}
          <Swiper
            onSwiper={(s) => {
              swiper.current = s;
            }}
            slidesPerView="auto"
            spaceBetween={5}
            freeMode={true}
            modules={[FreeMode]}
            pagination={false}
            preventInteractionOnTransition={false}
            allowTouchMove={isTouch}
            noSwiping={!isTouch}
            updateOnWindowResize
            onReachEnd={() => {
              setShowNext(false);
            }}
            onReachBeginning={() => {
              setShowPrev(false);
            }}
          >
            {moduleOptionalLayers.map((layer) => {
              return (
                <SwiperSlide key={layer.id}>
                  <MapFilterButton
                    key={layer.id}
                    onClick={() => handleLayerSelection(layer)}
                    selected={layer.visible}
                    opsColor={opsColor}
                    radius="4px"
                  >
                    <LayerIcon
                      config={config}
                      layer={layer}
                      t={t}
                      fill="#344054"
                      bgFill="#fff"
                      opsColor={opsColor}
                      selected={layer.visible}
                      width={26}
                      height={26}
                      showTitle={false}
                    />
                    <span>{getLayerTitle(layer, t)}</span>
                    <Tooltip
                      placement="bottom"
                      showOnHover
                      content={layer.getLayerInformation(t)}
                      blurred
                      absolute
                    />
                  </MapFilterButton>
                </SwiperSlide>
              );
            })}
            {hasFilters &&
              !!Object.keys(visibleFields).length &&
              Object.keys(visibleFields)?.map((field) => (
                <SwiperSlide key={field}>
                  <MapFilter
                    config={config}
                    view={view}
                    t={t}
                    field={visibleFields[field]}
                  />
                </SwiperSlide>
              ))}
          </Swiper>
          {showNext && (
            <MapFilterButton
              onClick={onNext}
              opsColor={opsColor}
              style={{
                marginBottom: 4,
                padding: 4,
                position: "absolute",
                right: "-22px",
                zIndex: 1,
                pointerEvents: "auto",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
              >
                <path d={chevronRight16} />
              </svg>
            </MapFilterButton>
          )}
        </>
      )}
    </ManualWidgetWrapper>
  );
};

export default ManualWidget;
